import { useEffect, useRef, useState } from "react";
import { api, formatApiError, UPLOADS_BASE } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DatabaseBackup, Upload, Mail, Download, RotateCcw, Loader2, History, HardDriveDownload } from "lucide-react";
import { toast } from "sonner";

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function BackupTab() {
  const [email, setEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [backups, setBackups] = useState([]);
  const [restores, setRestores] = useState([]);
  const [creating, setCreating] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [pin, setPin] = useState("");
  const [restoring, setRestoring] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const [{ data: emailData }, { data: listData }] = await Promise.all([
      api.get("/admin/backup/email"),
      api.get("/admin/backup/list"),
    ]);
    setEmail(emailData.email || "");
    setBackups(listData.backups || []);
    setRestores(listData.restores || []);
  };
  useEffect(() => { load(); }, []);

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await api.put("/admin/backup/email", { email });
      toast.success("Adresse e-mail de sauvegarde enregistrée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingEmail(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/backup/create");
      if (data.email_to) {
        if (data.email_sent) {
          toast.success(`Sauvegarde créée et envoyée par e-mail à ${data.email_to}`);
        } else {
          toast.warning(`Sauvegarde créée, mais l'envoi par e-mail a échoué : ${formatApiError(data.email_error)}`);
        }
      } else {
        toast.success("Sauvegarde créée (aucune adresse e-mail configurée pour l'envoi automatique)");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const pickRestoreFile = () => fileInputRef.current?.click();

  const onRestoreFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setRestoreFile(file);
    setRestoreOpen(true);
  };

  const confirmRestore = async () => {
    if (!restoreFile) return;
    if (!pin) {
      toast.error("Saisissez votre code PIN pour valider");
      return;
    }
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      fd.append("pin", pin);
      const { data } = await api.post("/admin/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message || "Site restauré");
      setRestoreOpen(false);
      setRestoreFile(null);
      setPin("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-muted-foreground">
        Créez une sauvegarde complète du site (base de données, fichiers déposés — PDF, photos, logos... — et code
        source) téléchargeable et envoyée par e-mail, ou restaurez une sauvegarde précédente. La restauration
        remplace le contenu actuel de la base de données et des fichiers déposés, et nécessite votre code PIN
        (celui utilisé pour vous connecter) pour être validée.
      </p>

      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <p className="font-heading font-semibold text-sm flex items-center gap-2"><Mail size={15} /> Adresse e-mail de sauvegarde</p>
        <p className="text-xs text-muted-foreground">
          À chaque sauvegarde créée, le fichier .zip est automatiquement envoyé à cette adresse.
        </p>
        <div className="flex items-center gap-2">
          <Input
            data-testid="backup-email-input"
            type="email"
            placeholder="ex: sauvegardes@votre-societe.fr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="button" size="sm" variant="outline" className="gap-1.5 whitespace-nowrap" disabled={savingEmail} onClick={saveEmail} data-testid="backup-email-save">
            <RotateCcw size={13} /> Enregistrer
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button type="button" onClick={createBackup} disabled={creating} className="gap-2" data-testid="backup-create-button">
          {creating ? <Loader2 size={16} className="animate-spin" /> : <DatabaseBackup size={16} />}
          {creating ? "Création en cours..." : "Créer une sauvegarde"}
        </Button>
        <Button type="button" variant="outline" onClick={pickRestoreFile} className="gap-2" data-testid="backup-restore-button">
          <Upload size={16} /> Restaurer une sauvegarde
        </Button>
        <input ref={fileInputRef} type="file" accept=".zip" className="hidden" onChange={onRestoreFileChange} data-testid="backup-restore-file-input" />
      </div>

      <div className="space-y-2">
        <p className="font-heading font-semibold text-sm flex items-center gap-2"><HardDriveDownload size={15} /> Sauvegardes</p>
        {backups.length === 0 && <p className="text-sm text-muted-foreground">Aucune sauvegarde pour le moment.</p>}
        <div className="space-y-2" data-testid="backup-list">
          {backups.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3" data-testid={`backup-row-${b.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{b.filename}</p>
                <p className="text-xs text-muted-foreground">{formatDate(b.created_at)} · {formatSize(b.size)} · {b.uploads_files ?? 0} fichier(s) déposé(s) · par {b.created_by_label}</p>
              </div>
              <a
                href={`${UPLOADS_BASE}${b.url}`}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 p-2 rounded-lg border border-border hover:bg-accent transition-colors duration-200"
                data-testid={`backup-download-${b.id}`}
                title="Télécharger"
              >
                <Download size={16} />
              </a>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="font-heading font-semibold text-sm flex items-center gap-2"><History size={15} /> Historique des restaurations</p>
        {restores.length === 0 && <p className="text-sm text-muted-foreground">Aucune restauration effectuée.</p>}
        <div className="space-y-2" data-testid="backup-restores-list">
          {restores.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-3" data-testid={`backup-restore-row-${r.id}`}>
              <p className="text-sm">{r.message}</p>
              <p className="text-xs text-muted-foreground mt-1">Par {r.user_label} · fichier : {r.source_filename} · {r.uploads_restored ?? 0} fichier(s) déposé(s) restauré(s)</p>
              {r.errors && r.errors.length > 0 && (
                <p className="text-xs text-amber-600 mt-1">{r.errors.join(" · ")}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={restoreOpen} onOpenChange={(o) => { setRestoreOpen(o); if (!o) { setRestoreFile(null); setPin(""); } }}>
        <DialogContent data-testid="backup-restore-dialog">
          <DialogHeader><DialogTitle>Restaurer une sauvegarde</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Cette action remplace immédiatement le contenu actuel de la base de données par celui du fichier
            <span className="font-medium"> {restoreFile?.name}</span>. Cette opération est irréversible (sauf à
            restaurer une sauvegarde plus récente ensuite).
          </p>
          <Input
            type="password"
            placeholder="Votre code PIN (celui utilisé pour vous connecter)"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            data-testid="backup-restore-pin"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)} data-testid="backup-restore-cancel">Annuler</Button>
            <Button onClick={confirmRestore} disabled={restoring} className="gap-2" data-testid="backup-restore-confirm">
              {restoring ? <Loader2 size={16} className="animate-spin" /> : null}
              {restoring ? "Restauration..." : "Confirmer la restauration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
